/**
 * Deterministic, seedable PRNG (mulberry32). 32-bit state, ~2^32 period.
 * Good enough for sample data; never use this for security purposes.
 *
 * `defaultRng()` falls back to Math.random when no seed is given — used
 * in UI "fill with sample data" buttons where we want fresh values
 * each click. For tests/snapshots, pass a seed.
 */

export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [min, max] inclusive on both ends. */
  int(min: number, max: number): number;
  /** Pick a uniformly random element from a non-empty array. */
  pick<T>(arr: readonly T[]): T;
  /** Pick `n` distinct elements from an array (clamped to arr length). */
  sample<T>(arr: readonly T[], n: number): T[];
  /** Bernoulli trial: returns true with probability p in [0, 1]. */
  chance(p: number): boolean;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createRng(seed: number): Rng {
  const rand = mulberry32(seed);
  return makeRng(rand);
}

export function defaultRng(): Rng {
  return makeRng(Math.random);
}

function makeRng(rand: () => number): Rng {
  function int(min: number, max: number): number {
    if (max < min) [min, max] = [max, min];
    return Math.floor(rand() * (max - min + 1)) + min;
  }
  function pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('pick: empty array');
    return arr[int(0, arr.length - 1)] as T;
  }
  function sample<T>(arr: readonly T[], n: number): T[] {
    const target = Math.min(n, arr.length);
    const copy = arr.slice();
    const out: T[] = [];
    for (let i = 0; i < target; i++) {
      const idx = int(0, copy.length - 1);
      out.push(copy[idx] as T);
      copy.splice(idx, 1);
    }
    return out;
  }
  function chance(p: number): boolean {
    return rand() < p;
  }
  return { next: rand, int, pick, sample, chance };
}
